import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  DiscoCallout,
  Flex,
  Heading,
  SearchTextField,
} from 'components/ds'
import { DiscoModal } from 'components/ui'
import { RightsDataTable } from 'components/common/tracks/metadata-fields/rights-data/rights-data-table'
import { RightsData } from '@disco/types/types/rights-data'
import {
  provideRightsDataFeedback,
  requestRightsData,
} from '@disco/api/rights-data'
import LoadingIndicator from 'components/ui/loading-indicator'
import { useSelector } from 'hooks/use-redux'
import { Text } from '@radix-ui/themes'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { rightsDataModalStyles } from 'components/utils/modalStyles'
import { getTrack } from '@disco/state-management/selectors/tracks'
import type { RightsDataUsageFeedback } from './rights-data-feedback'
import RightsDataFeedback from './rights-data-feedback'

type Props = {
  trackId: number
  handleClose: () => any
}

type ErrorKind = 'No Data' | 'Failed'

const RightsDataModal = (props: Props) => {
  const track = useSelector(state =>
    getTrack(state, {
      id: props.trackId,
    }),
  )
  const [rightsData, setRightsData] = useState<RightsData>(null)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorKind | null>(null)
  const [feedback, setFeedback] = useState<RightsDataUsageFeedback>({
    isHelpful: null,
    comment: '',
  })

  const handleFeedbackChange = useCallback(
    (newFeedback: RightsDataUsageFeedback) => {
      setFeedback(newFeedback)
    },
    [],
  )

  const handleSubmitAndClose = useCallback(() => {
    if (feedback.isHelpful !== null) {
      provideRightsDataFeedback(
        feedback.isHelpful,
        feedback.comment,
        rightsData.rights_usage_id,
      )
    }

    props.handleClose()
  }, [feedback, props, rightsData])

  const feedbackGiven = feedback.isHelpful !== null
  const buttonText = feedbackGiven ? 'Submit feedback and Close' : 'Close'

  const constanceSettings = useSelector(getConstanceSettings)
  const rightsDataEnabled = constanceSettings?.THIRD_PARTY_RIGHTS_DATA || false

  const fetchRightsData = async () => {
    setLoading(true)
    try {
      const rightsData = await requestRightsData(props.trackId)
      setRightsData(rightsData)
    } catch (e) {
      if (e.status === 404) {
        setError('No Data')
      } else {
        setError('Failed')
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    if (rightsDataEnabled && !rightsData && !error) {
      fetchRightsData()
    }
  }, [rightsDataEnabled, rightsData, error])

  const getByPlaceholderText = (rightsData: RightsData) => {
    let searchedBy = `Searching for rights data for track`

    if (track) {
      if (track.metadata.ISRC) {
        return `Searching by ISRC: ${track.metadata.ISRC}`
      }
      if (track.artist) {
        return `Searching by Title: ${track.name}, Artist: ${track.artist}`
      }

      return `Searching for track: ${track.name}`
    }

    if (!rightsData || !rightsData.query) {
      return searchedBy
    }

    if ('isrc' in rightsData.query) {
      searchedBy = `ISRC: ${rightsData.query.isrc}`
    } else {
      searchedBy = `Title: ${rightsData.query.title}, Artist: ${rightsData.query.artists[0]}`
    }

    return `Searching by ${searchedBy}`
  }

  const noResultsISRC = (
    <DiscoCallout type="info">
      We weren’t able to return any results for this search. This can happen if
      the ISRC is incorrect. In some cases, there may simply be no third-party
      data available for this track.
    </DiscoCallout>
  )

  const noResultsTitleArtist = (
    <DiscoCallout type="info">
      We weren’t able to return any results for this search. This can happen for
      a few reasons - for example, the title or artist might contain spelling
      errors or extra text. In some cases, there may simply be no third-party
      data available for this track.
    </DiscoCallout>
  )

  const errorCallout = (
    <DiscoCallout type="error">
      Something went wrong. Please try searching again later.
    </DiscoCallout>
  )

  let resultCallout = null
  if (error) {
    if (error === 'Failed') {
      resultCallout = errorCallout
    } else if (track.metadata.ISRC) {
      resultCallout = noResultsISRC
    } else {
      resultCallout = noResultsTitleArtist
    }
  }

  return (
    <DiscoModal
      onRequestClose={props.handleClose}
      style={rightsDataModalStyles}
    >
      <Heading mr="64px">
        <Text size="5">Rights Data Lookup</Text>
      </Heading>
      {!rightsDataEnabled ? (
        <DiscoCallout type="error">
          Rights data feature is not enabled.
        </DiscoCallout>
      ) : (
        <>
          <SearchTextField
            mb="4"
            disabled={true}
            placeholder={getByPlaceholderText(rightsData)}
          />
          {isLoading && (
            <>
              <Flex justify="center">
                <LoadingIndicator />
              </Flex>
              <Flex justify="center">
                <Text>Fetching Rights Data</Text>
              </Flex>
            </>
          )}
          {rightsData && (
            <>
              <DiscoCallout type="info">
                The rights information provided below has been obtained from
                third-party sources, and is provided in order to supplement your
                search. DISCO does not guarantee that these results are either
                complete or up to date. Please double-check the details before
                relying on them for legal or licensing purposes.
              </DiscoCallout>
              <RightsDataTable rightsData={rightsData} />
              <RightsDataFeedback onFeedbackChange={handleFeedbackChange} />
            </>
          )}
          {!rightsData && error && resultCallout}
        </>
      )}
      <Box>
        <Flex justify="end" mt="2">
          <Button
            color="gray"
            highContrast
            size="2"
            onClick={handleSubmitAndClose}
          >
            {buttonText}
          </Button>
        </Flex>
      </Box>
    </DiscoModal>
  )
}

export default RightsDataModal
